const asArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value == null) return [];
    return [value];
};

const toNumber = (value) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
};

const cleanString = (value) => (
    typeof value === 'string' && value.trim() ? value.trim() : null
);

export const getClassScheduleLabel = (course = {}) => {
    const period = cleanString(course.period) || cleanString(course.section) || cleanString(course.block);
    const grade =
        cleanString(course.gradeLevel) ||
        cleanString(course.grade_level) ||
        cleanString(course.grade);

    return [period && `Period ${period}`, grade && `Grade ${grade}`].filter(Boolean).join(' - ') || 'Schedule not set';
};

export const getGradeCadenceStatus = (course = {}) => {
    const cadence = course.gradeCadence || course.grade_cadence || {};
    const minorCompleted = toNumber(cadence.minorCompleted ?? cadence.minor_completed);
    const minorRequired = toNumber(cadence.minorRequired ?? cadence.minor_required);
    const majorCompleted = toNumber(cadence.majorCompleted ?? cadence.major_completed);
    const majorRequired = toNumber(cadence.majorRequired ?? cadence.major_required);
    const minorRemaining = Math.max(minorRequired - minorCompleted, 0);
    const majorRemaining = Math.max(majorRequired - majorCompleted, 0);

    return {
        label: cleanString(cadence.windowLabel) || cleanString(cadence.window_label) || 'Current grade window',
        minorText: `${minorCompleted}/${minorRequired} minor`,
        majorText: `${majorCompleted}/${majorRequired} major`,
        minorRemaining,
        majorRemaining,
        needsAttention: minorRemaining > 0 || majorRemaining > 0,
    };
};

export const getCourseOperationalStatus = (course = {}) => {
    const cadence = getGradeCadenceStatus(course);
    const urgency = cleanString(course.urgency) || 'low';

    if (urgency === 'high') {
        return {
            tone: 'urgent',
            label: 'Act today',
            detail: cleanString(course.priorityReason) || cleanString(course.priority_reason) || 'This class has a pacing, safety, or grading need.',
        };
    }

    if (cadence.needsAttention) {
        return {
            tone: 'attention',
            label: 'Grade window needs evidence',
            detail: `${cadence.minorRemaining} minor and ${cadence.majorRemaining} major grade opportunities remaining.`,
        };
    }

    return {
        tone: 'steady',
        label: 'On track',
        detail: cleanString(course.status) || 'No immediate operating blocker is visible.',
    };
};

export const getCourseActionPlan = (course = {}) => {
    const explicitActions = asArray(course.nextActions || course.next_actions).filter(Boolean);
    const prepItems = asArray(course.teacherPrepChecklist || course.teacher_prep_checklist).filter(Boolean);
    const checkpoint = course.projectCheckpoint || course.project_checkpoint || {};
    const checkpointAction =
        cleanString(checkpoint.studentDeliverable) ||
        cleanString(checkpoint.gradeOpportunity) ||
        cleanString(checkpoint.teacherPrep);

    return [...explicitActions, ...prepItems, checkpointAction]
        .filter(Boolean)
        .slice(0, 4);
};

export const getClassOperationalRows = (courses = []) => (
    asArray(courses).map((course, index) => ({
        id: cleanString(course.id) || cleanString(course.course_id) || `course-${index + 1}`,
        name: cleanString(course.name) || cleanString(course.course_name) || cleanString(course.subject) || `Course ${index + 1}`,
        scheduleLabel: getClassScheduleLabel(course),
        status: getCourseOperationalStatus(course),
        cadence: getGradeCadenceStatus(course),
        nextMove:
            cleanString(course.bestNextMove) ||
            cleanString(course.best_next_move) ||
            cleanString(course.suggestedSaigeAction) ||
            'Choose the next teacher-facing planning move.',
        actionPlan: getCourseActionPlan(course),
        source: cleanString(course.source) || 'operating-state',
        raw: course,
    }))
);
